import { calculateB31G_FailureStress, calculateB31GModified_FailureStress, calculateRSTRENG_FailureStress } from '../../utils/b31gCalculations';
import { calculateMAOP } from '../../utils/maopCalculations';
import DataGrid from './DataGrid';
import { useState, useEffect } from 'react';

function Anomaly({ anomaly, index, flowStress, designFactor, outsideDiameter, onUpdate, onRemove }) {
  const [profileSpacing, setProfileSpacing] = useState('');
  const [readingType, setReadingType] = useState('depth');
  const [measurementType, setMeasurementType] = useState(anomaly.measurementType || 'singleDepth');
  const [numberOfReadings, setNumberOfReadings] = useState(10);
  const [profileMinValue, setProfileMinValue] = useState('');
  const [profileMaxValue, setProfileMaxValue] = useState('');
  const [profilePositions, setProfilePositions] = useState([]);
  const [profileDepths, setProfileDepths] = useState([]);
  let remainingThickness = 0;

  useEffect(() => {
    setMeasurementType(anomaly.measurementType || 'singleDepth');
  }, [anomaly.measurementType]);

  const handleFieldChange = (field, value) => {
    onUpdate(anomaly.id, field, value);
  };

  const handleProfileSpacingChange = (value) => {
    setProfileSpacing(value);
  };

  const handleReadingTypeChange = (value) => {
    setReadingType(value);
  };

  const handleMeasurementTypeChange = (value) => {
    setMeasurementType(value);
    handleFieldChange('measurementType', value);
  };

  const handleNumberOfReadingsChange = (value) => {
    setNumberOfReadings(parseInt(value) || 10);
  };

  const handleProfileMinChange = (minValue) => {
    setProfileMinValue(minValue);
    if (readingType === 'thickness') {
      const wallThickness = parseFloat(anomaly.wallThickness) || 0;
      remainingThickness = minValue === '' ? NaN : parseFloat(minValue);
      const depth = Number.isNaN(remainingThickness)
        ? ''
        : (wallThickness - remainingThickness).toFixed(2);
      handleFieldChange('depth', depth);
    }
  };

  const handleProfileMaxChange = (maxValue) => {
    setProfileMaxValue(maxValue);
    if (readingType === 'depth') {
      const depth = Number.isNaN(maxValue)
        ? ''
        : maxValue;
      handleFieldChange('depth', depth);
    }
  }

  const handleRemainingThicknessChange = (value) => {
    remainingThickness = parseFloat(value) || 0;
    const wallThickness = parseFloat(anomaly.wallThickness) || 0;
    const depth = wallThickness - remainingThickness;
    handleFieldChange('depth', depth.toString());
  };

  const handleProfilePositionsChange = (positions) => {
    setProfilePositions(positions);
    // Save profile data to anomaly
    handleFieldChange('profileGridData', { positions, depths: profileDepths });
  };

  const handleProfileDepthsChange = (depths) => {
    setProfileDepths(depths);
    // Save profile data to anomaly
    handleFieldChange('profileGridData', { positions: profilePositions, depths });
  };

  const isDepthProfileVisible = measurementType === 'depthProfile';

  // Load saved profile data when switching to depthProfile
  useEffect(() => {
    if (isDepthProfileVisible && anomaly.profileGridData) {
      const { positions, depths } = anomaly.profileGridData;
      if (positions && positions.length > 0) {
        setProfilePositions(positions);
      }
      if (depths && depths.length > 0) {
        setProfileDepths(depths);
      }
    }
  }, [isDepthProfileVisible, anomaly.profileGridData]);

  useEffect(() => {
    if (isDepthProfileVisible && readingType === 'thickness' && profileMinValue !== '') {
      const wallThickness = parseFloat(anomaly.wallThickness) || 0;
      if (readingType === 'thickness') {
        remainingThickness = parseFloat(profileMinValue);
      }else{
        remainingThickness = wallThickness - parseFloat(profileMinValue);
      }
      if (!Number.isNaN(remainingThickness)) {
        handleFieldChange('depth', (wallThickness - remainingThickness).toFixed(2));
      }
    }
  }, [anomaly.wallThickness, readingType, profileMinValue, isDepthProfileVisible]);

  // Calculate length from difference between max and min positions in depth profile
  useEffect(() => {
    if (isDepthProfileVisible && profilePositions.length > 0) {
      const minPosition = Math.min(...profilePositions);
      const maxPosition = Math.max(...profilePositions);
      const length = (maxPosition - minPosition).toFixed(2);
      handleFieldChange('length', length);
    }
  }, [profilePositions, isDepthProfileVisible]);

  // Update depth based on profile depths
  useEffect(() => {
    if (isDepthProfileVisible && profileDepths.length > 0) {
      if (readingType === 'depth') {
        const maxDepth = Math.max(...profileDepths);
        handleFieldChange('depth', maxDepth.toFixed(2));
      } else if (readingType === 'thickness') {
        const minThickness = Math.min(...profileDepths);
        const wallThickness = parseFloat(anomaly.wallThickness) || 0;
        const depth = wallThickness - minThickness;
        handleFieldChange('depth', depth.toFixed(2));
      }
    }
  }, [profileDepths, readingType, anomaly.wallThickness, isDepthProfileVisible]);

  const b31g_FS = calculateB31G_FailureStress(anomaly.depth, anomaly.length, anomaly.wallThickness, outsideDiameter, flowStress);
  const b31gModified_FS = calculateB31GModified_FailureStress(anomaly.depth, anomaly.length, anomaly.wallThickness, outsideDiameter, flowStress);
  const rstreng_FS = calculateRSTRENG_FailureStress(profilePositions, profileDepths, anomaly.wallThickness, outsideDiameter, flowStress);
  //const rstreng_FS = 0;
  
  const maopB31G = calculateMAOP(designFactor, b31g_FS, anomaly.wallThickness, outsideDiameter);
  const maopB31GModified = calculateMAOP(designFactor, b31gModified_FS, anomaly.wallThickness, outsideDiameter);
  const maopRSTRENG = calculateMAOP(designFactor, rstreng_FS, anomaly.wallThickness, outsideDiameter);

  //const isDepthEditable = !isDepthProfileVisible && readingType === 'depth';
  //const isRemainingThicknessEditable = !isDepthProfileVisible && readingType === 'thickness';

  const depthValue = isDepthProfileVisible
    ? readingType === 'thickness'
      ? profileMinValue !== ''
        ? (parseFloat(anomaly.wallThickness || 0) - parseFloat(profileMinValue)).toFixed(2)
        : anomaly.depth
      : profileMinValue || anomaly.depth
    : anomaly.depth;

  const remainingThicknessValue = isDepthProfileVisible
    ? readingType === 'thickness'
      ? profileMinValue || (parseFloat(anomaly.wallThickness || 0) - parseFloat(anomaly.depth || 0)).toFixed(2)
      : (parseFloat(anomaly.wallThickness || 0) - parseFloat(depthValue || 0)).toFixed(2)
    : (parseFloat(anomaly.wallThickness || 0) - parseFloat(anomaly.depth || 0)).toFixed(2);

  return (
    <div className="anomaly-card">
      <div className="anomaly-header">
        <h3>Anomaly {index + 1}</h3>
        <button
          className="remove-button"
          onClick={() => onRemove(anomaly.id)}
        >
          ✕
        </button>
      </div>
      <div className="anomaly-fields">
        <div className="form-group">
          <label htmlFor={`desc-${anomaly.id}`}>Description</label>
          <input
            id={`desc-${anomaly.id}`}
            type="text"
            value={anomaly.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="Enter description"
          />
        </div>

        <div className="form-group">
          <label htmlFor={`thickness-${anomaly.id}`}>Wall Thickness</label>
          <input
            id={`thickness-${anomaly.id}`}
            type="number"
            step="0.01"
            value={anomaly.wallThickness}
            onChange={(e) => handleFieldChange('wallThickness', e.target.value)}
            placeholder="0.0"
          />
        </div>

        <fieldset className="measurement-options">
          <legend>Reading Type</legend>
          <label className="radio-label">
            <input
              type="radio"
              name={`readingType-${anomaly.id}`}
              value="depth"
              checked={readingType === 'depth'}
              onChange={() => handleReadingTypeChange('depth')}
            />
            Depth
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name={`readingType-${anomaly.id}`}
              value="thickness"
              checked={readingType === 'thickness'}
              onChange={() => handleReadingTypeChange('thickness')}
            />
            Thickness
          </label>
        </fieldset>

        <fieldset className="measurement-options">
          <legend>Measurement Type</legend>
          <label className="radio-label">
            <input
              type="radio"
              name={`measurementType-${anomaly.id}`}
              value="singleDepth"
              checked={measurementType === 'singleDepth'}
              onChange={() => handleMeasurementTypeChange('singleDepth')}
            />
            Single Depth Measurement
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name={`measurementType-${anomaly.id}`}
              value="depthProfile"
              checked={measurementType === 'depthProfile'}
              onChange={() => handleMeasurementTypeChange('depthProfile')}
            />
            Depth Profile
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name={`measurementType-${anomaly.id}`}
              value="grid2d"
              checked={measurementType === 'grid2d'}
              onChange={() => handleMeasurementTypeChange('grid2d')}
            />
            2D Measurement Grid
          </label>
        </fieldset>
        
        <div className="form-group">
          <label htmlFor={`depth-${anomaly.id}`}>Depth</label>
          <input
            id={`depth-${anomaly.id}`}
            type="number"
            step="0.01"
            value={anomaly.depth}
            onChange={(e) => handleFieldChange('depth', e.target.value)}
            placeholder="0.0"
            readOnly={readingType !== 'depth'}
            style={{
              color: parseFloat(anomaly.depth) >= parseFloat(anomaly.wallThickness) ? 'red' : 'black',
              backgroundColor: readingType !== 'depth' ? '#f5f5f5' : 'white'
            }}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor={`remaining-thickness-${anomaly.id}`}>Remaining Thickness</label>
          <input
            id={`remaining-thickness-${anomaly.id}`}
            type="number"
            step="0.01"
            value={(parseFloat(anomaly.wallThickness || 0) - parseFloat(anomaly.depth || 0)).toFixed(2)}
            onChange={(e) => handleRemainingThicknessChange(e.target.value)}
            placeholder="0.0"
            readOnly={readingType !== 'thickness'}
            style={{
              backgroundColor: readingType !== 'thickness' ? '#f5f5f5' : 'white'
            }}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor={`length-${anomaly.id}`}>Length</label>
          <input
            id={`length-${anomaly.id}`}
            type="number"
            step="0.01"
            value={anomaly.length}
            onChange={(e) => handleFieldChange('length', e.target.value)}
            placeholder="0.0"
          />
        </div>
      </div>

      {measurementType === 'depthProfile' && (
        <div className="profile-section">
          <h4>Profile</h4>
          <div className="profile-controls">
            <div className="form-group">
              <label htmlFor="profile-spacing">Profile Spacing</label>
              <input
                id="profile-spacing"
                type="number"
                step="0.01"
                min="0"
                value={profileSpacing}
                onChange={(e) => handleProfileSpacingChange(e.target.value)}
                placeholder="0.0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="number-of-readings">Number of Readings</label>
              <input
                id="number-of-readings"
                type="number"
                min="1"
                value={numberOfReadings}
                onChange={(e) => handleNumberOfReadingsChange(e.target.value)}
                placeholder="10"
              />
            </div>
            <DataGrid
              profileSpacing={profileSpacing}
              numberOfReadings={numberOfReadings}
              readingType={readingType}
              onProfileMinChange={handleProfileMinChange}
              onProfileMaxChange={handleProfileMaxChange}
              onPositionsChange={handleProfilePositionsChange}
              onDepthsChange={handleProfileDepthsChange}
              initialData={anomaly.profileGridData}
            />
          </div>
        </div>
      )}

      <div className="anomaly-results">
        <div className="result-group">
          <label>B31G Failure Stress</label>
          <span>{b31g_FS}</span>
        </div>
        <div className="result-group">
          <label>MAOP B31G</label>
          <span>{maopB31G}</span>
        </div>
        <div className="result-group">
          <label>B31G Modified Failure Stress</label>
          <span>{b31gModified_FS}</span>
        </div>
        <div className="result-group">
          <label>MAOP B31G Modified</label>
          <span>{maopB31GModified}</span>
        </div>
        {measurementType === 'depthProfile' && (
          <><div className="result-group">
            <label>RSTRENG Failure Stress</label>
            <span>{rstreng_FS}</span>
          </div><div className="result-group">
              <label>RSTRENG MAOP</label>
              <span>{maopRSTRENG}</span>
            </div></>
        )}

      </div>


    </div>
  );
}

export default Anomaly;
